import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import { Flipped, Flipper } from "react-flip-toolkit";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  MiAlert,
  MiButton,
  MiDetailFields,
  MiReorderButton,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DATE_TIME_FORMATS,
  INPUT_TYPES,
  SWAP_DIRECTION,
} from "@miplatform/constants";
import { ICalendarSet } from "@miplatform/types";
import { mdiMinus, mdiPlus } from "@mdi/js";
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import {
  IWorkOrderResourceAssignment,
  IWorkOrderTemplateData,
} from "../ConfigWorkOrderTemplates.types";
import {
  getOverlappingWindowIndices,
  haveAssignmentsChanged,
  matchesAssignmentUser,
  normalizeAssignments,
  sortAssignmentsByDisplayOrder,
  withDisplayOrder,
} from "../configWorkOrderTemplates.util";

type ParsedRatioResult =
  | { isValid: true; parsedValue: number }
  | { isValid: false; message: string };

const defaultAutoAssignmentWindow = {
  start: null,
  end: null,
};

export const WorkOrderAssignmentRulesTabView: FC<
  WorkOrderAssignmentRulesTabViewProps
> = ({
  form,
  extraTabProps: { isReadOnly, calendarSets = [] } = {
    isReadOnly: false,
    calendarSets: [],
  },
}) => {
  const { classes } = useStyles();
  const {
    watch,
    setValue,
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = form;
  const {
    autoAssign,
    orderTemplateId,
    calendarSetId,
    resourceAssignments = [],
    allDayAssignment = true,
    autoAssignmentWindows = [],
  } = watch();
  const [ratioDrafts, setRatioDrafts] = useState<Record<string, string>>({});

  const selectedCalendarSet = useMemo(
    () => calendarSets.find((calendarSet) => calendarSet.id === calendarSetId),
    [calendarSets, calendarSetId]
  );

  const assignableUsers = selectedCalendarSet?.assignableUsers ?? [];
  const hasSelectedOrderTemplate = Boolean(orderTemplateId);
  const hasSelectedCalendarSet = Boolean(calendarSetId);
  const hasAssignableUsers = assignableUsers.length > 0;
  const canConfigureAutoAssign =
    hasSelectedOrderTemplate && hasSelectedCalendarSet && hasAssignableUsers;
  const autoAssignControlsDisabled =
    isReadOnly || !autoAssign || !canConfigureAutoAssign;
  const overlappingWindowIndices = useMemo(
    () => getOverlappingWindowIndices(autoAssignmentWindows),
    [autoAssignmentWindows]
  );
  const orderedAssignments = useMemo(
    () => sortAssignmentsByDisplayOrder(resourceAssignments ?? []),
    [resourceAssignments]
  );
  const ratioSignature = resourceAssignments
    .map(
      ({ resourceId, autoAssignmentRatio, displayOrder }) =>
        `${resourceId}:${autoAssignmentRatio}:${displayOrder ?? ""}`
    )
    .join("|");
  const getUserDisplayName = (user: (typeof assignableUsers)[number]) =>
    user.displayName ||
    (user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : null) ||
    user.name ||
    user.email;

  const zeroRatioUsers = useMemo(
    () =>
      orderedAssignments
        .filter((assignment) => assignment.autoAssignmentRatio === 0)
        .map((assignment) => {
          const assignableUser = assignableUsers.find(
            (user) =>
              user.id === assignment.resourceId ||
              user.resourceId === assignment.resourceId
          );

          return assignableUser ? getUserDisplayName(assignableUser) : null;
        })
        .filter((userName): userName is string => Boolean(userName)),
    [assignableUsers, orderedAssignments]
  );
  const autoAssignPrerequisites = [
    !hasSelectedOrderTemplate
      ? "Attach an Order Template before enabling auto-assign."
      : null,
    !hasSelectedCalendarSet
      ? "Select a Calendar Set before enabling auto-assign."
      : null,
    hasSelectedCalendarSet && !hasAssignableUsers
      ? "The selected Calendar Set does not have any assignable users yet."
      : null,
  ].filter((message): message is string => Boolean(message));

  const getMatchingAssignableUser = (resourceId: string) =>
    assignableUsers.find((user) => matchesAssignmentUser(resourceId, user));

  const setAssignments = (
    assignments: IWorkOrderResourceAssignment[],
    shouldDirty: boolean
  ) => {
    setValue("resourceAssignments", assignments, {
      shouldDirty,
      shouldValidate: true,
    });
  };

  const setAssignmentWindows = (
    windows: typeof autoAssignmentWindows,
    shouldDirty: boolean
  ) => {
    setValue("autoAssignmentWindows", windows, {
      shouldDirty,
      shouldValidate: true,
    });
  };

  const updateRatioAtIndex = (index: number, autoAssignmentRatio: number) => {
    const assignmentToUpdate = orderedAssignments[index];
    if (!assignmentToUpdate) {
      return;
    }

    const updatedAssignments = orderedAssignments.map((assignment) =>
      assignment.resourceId === assignmentToUpdate.resourceId
        ? {
            ...assignment,
            autoAssignmentRatio,
          }
        : assignment
    );

    setAssignments(updatedAssignments, true);
  };

  const setRatioFieldError = (index: number, message: string) => {
    setError(`resourceAssignments.${index}.autoAssignmentRatio`, {
      type: "manual",
      message,
    });
  };

  const clearRatioFieldError = (index: number) => {
    clearErrors(`resourceAssignments.${index}.autoAssignmentRatio`);
  };

  const parseRatioValue = (value: string): ParsedRatioResult => {
    if (value.trim() === "") {
      return { isValid: false, message: "Ratio is required." };
    }

    const parsedValue = Number(value);

    if (!Number.isFinite(parsedValue)) {
      return {
        isValid: false,
        message: "Ratio must be a valid non-negative number.",
      };
    }

    if (parsedValue < 0) {
      return {
        isValid: false,
        message: "Ratio cannot be negative.",
      };
    }

    return {
      isValid: true,
      parsedValue,
    };
  };

  useEffect(() => {
    const users = selectedCalendarSet?.assignableUsers ?? [];
    if (users.length === 0) {
      if (resourceAssignments.length > 0) {
        setAssignments([], false);
      }

      return;
    }

    const existingAssignments = resourceAssignments.flatMap((assignment) => {
      const matchingUser = users.find((user) =>
        matchesAssignmentUser(assignment.resourceId, user)
      );

      if (!matchingUser) {
        return [];
      }

      return [
        {
          resourceId: matchingUser.id,
          autoAssignmentRatio: assignment.autoAssignmentRatio,
          performanceMultiplierPercentage:
            assignment.performanceMultiplierPercentage ?? 0,
          displayOrder: assignment.displayOrder,
        },
      ];
    });

    const newAssignments = users
      .filter(
        (user) =>
          !resourceAssignments.some((assignment) =>
            matchesAssignmentUser(assignment.resourceId, user)
          )
      )
      .map((user, index) => ({
        resourceId: user.id,
        autoAssignmentRatio: 1,
        performanceMultiplierPercentage: 0,
        displayOrder: resourceAssignments.length + index + 1,
      }));

    const normalizedAssignments = normalizeAssignments([
      ...existingAssignments,
      ...newAssignments,
    ]);

    if (haveAssignmentsChanged(orderedAssignments, normalizedAssignments)) {
      setAssignments(normalizedAssignments, false);
    }
  }, [selectedCalendarSet, resourceAssignments, setValue]);

  useEffect(() => {
    setRatioDrafts(
      Object.fromEntries(
        orderedAssignments.map((assignment) => [
          assignment.resourceId,
          assignment.autoAssignmentRatio.toString(),
        ])
      )
    );
  }, [ratioSignature]);

  useEffect(() => {
    if (!canConfigureAutoAssign && autoAssign) {
      setValue("autoAssign", false, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [autoAssign, canConfigureAutoAssign, setValue]);

  const handleMoveAssignment = (
    index: number,
    direction: (typeof SWAP_DIRECTION)[keyof typeof SWAP_DIRECTION]
  ): void => {
    const newAssignments = [...orderedAssignments];
    const [movedItem] = newAssignments.splice(index, 1);

    const newIndex = direction === SWAP_DIRECTION.UP ? index - 1 : index + 1;
    newAssignments.splice(newIndex, 0, movedItem);

    setAssignments(withDisplayOrder(newAssignments), true);
  };

  const handleRatioChange = (
    index: number,
    resourceId: string,
    value: string
  ) => {
    setRatioDrafts((currentDrafts) => ({
      ...currentDrafts,
      [resourceId]: value,
    }));

    const parsedRatio = parseRatioValue(value);

    if (!parsedRatio.isValid) {
      setRatioFieldError(index, parsedRatio.message);
      return;
    }

    clearRatioFieldError(index);
    updateRatioAtIndex(index, parsedRatio.parsedValue);
  };

  const handleRatioBlur = (
    index: number,
    resourceId: string,
    value: string
  ) => {
    const parsedRatio = parseRatioValue(value);

    if (!parsedRatio.isValid) {
      setRatioFieldError(index, parsedRatio.message);
      return;
    }

    clearRatioFieldError(index);
    updateRatioAtIndex(index, parsedRatio.parsedValue);
    setRatioDrafts((currentDrafts) => ({
      ...currentDrafts,
      [resourceId]: parsedRatio.parsedValue.toString(),
    }));
  };

  const handleAllDayAssignmentChange = (value: string) => {
    const isAllDayAssignment = value === "true";

    setValue("allDayAssignment", isAllDayAssignment, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (isAllDayAssignment) {
      setAssignmentWindows([], true);
      return;
    }

    if (autoAssignmentWindows.length === 0) {
      setAssignmentWindows([{ ...defaultAutoAssignmentWindow }], true);
    }
  };

  const handleWindowChange = (
    index: number,
    field: "start" | "end",
    formattedValue: string
  ) => {
    const time = /^\d{2}:\d{2}$/.test(formattedValue)
      ? `${formattedValue}:00`
      : formattedValue;
    const nextWindow = {
      ...(autoAssignmentWindows[index] ?? defaultAutoAssignmentWindow),
      [field]: time,
    };

    const updatedWindows = [...autoAssignmentWindows];
    updatedWindows[index] = nextWindow;

    setAssignmentWindows(updatedWindows, true);
  };

  const handleAddWindow = () => {
    setAssignmentWindows(
      [...autoAssignmentWindows, { ...defaultAutoAssignmentWindow }],
      true
    );
  };

  const handleRemoveWindow = (index: number) => {
    const updatedWindows = autoAssignmentWindows.filter(
      (_, windowIndex) => windowIndex !== index
    );

    setAssignmentWindows(updatedWindows, true);
  };

  const autoAssignField = [
    {
      name: "autoAssign",
      formControl: control,
      fieldType: INPUT_TYPES.SWITCH,
      disabled: isReadOnly || !canConfigureAutoAssign,
      width: "100%",
      label: (
        <Typography variant="body2">
          <Typography component="span" variant="body2" fontWeight={600}>
            Auto-Assign:
          </Typography>{" "}
          Allows for the work order to be automatically assigned to an available
          user.
        </Typography>
      ),
      handleChange: ({
        target: { checked },
      }: ChangeEvent<HTMLInputElement>) => {
        setValue("autoAssign", checked, {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
    },
  ];

  const getCustomTimeWindowFields = (index: number) => {
    const timeWindow =
      autoAssignmentWindows[index] ?? defaultAutoAssignmentWindow;

    return [
      {
        label: "Start Time",
        name: `autoAssignmentWindows.${index}.start`,
        fieldType: INPUT_TYPES.TIME_PICKER,
        format: DATE_TIME_FORMATS.FORMAT_24_HOUR,
        value: timeWindow.start ?? null,
        handleChange: (formattedValue: string) => {
          handleWindowChange(index, "start", formattedValue);
        },
        disabled: autoAssignControlsDisabled,
        width: "50%",
      },
      {
        label: "End Time",
        name: `autoAssignmentWindows.${index}.end`,
        fieldType: INPUT_TYPES.TIME_PICKER,
        format: DATE_TIME_FORMATS.FORMAT_24_HOUR,
        value: timeWindow.end ?? null,
        handleChange: (formattedValue: string) => {
          handleWindowChange(index, "end", formattedValue);
        },
        disabled: autoAssignControlsDisabled,
        width: "50%",
      },
    ];
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <ConfigAccordionHeader title="Auto-Assignment">
        <MiDetailFields detailFields={autoAssignField} />
        {autoAssignPrerequisites.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {autoAssignPrerequisites.join(" ")}
          </Alert>
        )}
      </ConfigAccordionHeader>

      <ConfigAccordionHeader title="Auto-Assignment Ratios">
        {orderedAssignments.length === 0 ? null : (
          <Flipper
            flipKey={orderedAssignments
              .map(
                (assignment) =>
                  `${assignment.resourceId}:${assignment.displayOrder}`
              )
              .join("-")}
            className={classes.list}
          >
            {orderedAssignments.map((entry, index) => {
              const assignableUser = getMatchingAssignableUser(
                entry.resourceId
              );
              const displayName = assignableUser
                ? getUserDisplayName(assignableUser)
                : "Assignable User";
              const ratioError =
                errors.resourceAssignments?.[index]?.autoAssignmentRatio
                  ?.message;

              return (
                <Flipped key={entry.resourceId} flipId={entry.resourceId}>
                  {(flippedProps) => (
                    <div {...flippedProps}>
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        width="100%"
                        gap={2}
                      >
                        <TextField
                          label={displayName}
                          value={
                            ratioDrafts[entry.resourceId] ??
                            entry.autoAssignmentRatio.toString()
                          }
                          onChange={(e) =>
                            handleRatioChange(
                              index,
                              entry.resourceId,
                              e.target.value
                            )
                          }
                          onBlur={(e) =>
                            handleRatioBlur(
                              index,
                              entry.resourceId,
                              e.target.value
                            )
                          }
                          type="text"
                          size="small"
                          disabled={autoAssignControlsDisabled}
                          inputProps={{ inputMode: "decimal" }}
                          error={Boolean(ratioError)}
                          helperText={
                            ratioError ??
                            (entry.autoAssignmentRatio === 0
                              ? `No work orders will be auto assigned to ${displayName}`
                              : undefined)
                          }
                          FormHelperTextProps={{
                            sx:
                              !ratioError && entry.autoAssignmentRatio === 0
                                ? { color: "warning.main" }
                                : undefined,
                          }}
                          sx={{ flex: 1 }}
                        />
                        <MiReorderButton
                          onMoveUp={() =>
                            handleMoveAssignment(index, SWAP_DIRECTION.UP)
                          }
                          onMoveDown={() =>
                            handleMoveAssignment(index, SWAP_DIRECTION.DOWN)
                          }
                          disableUp={
                            isReadOnly ||
                            !autoAssign ||
                            !canConfigureAutoAssign ||
                            index === 0
                          }
                          disableDown={
                            isReadOnly ||
                            !autoAssign ||
                            !canConfigureAutoAssign ||
                            index === orderedAssignments.length - 1
                          }
                        />
                      </Box>
                    </div>
                  )}
                </Flipped>
              );
            })}
          </Flipper>
        )}
        {hasSelectedCalendarSet && !hasAssignableUsers && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This calendar set does not have any assignable users configured yet.
            Add assignable users to the calendar set to use auto-assignment
            ratios.
          </Typography>
        )}
        {zeroRatioUsers.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {zeroRatioUsers.length === 1
              ? `${zeroRatioUsers[0]} has a ratio of 0 and will not receive auto-assigned work orders.`
              : `${zeroRatioUsers.join(
                  ", "
                )} have a ratio of 0 and will not receive auto-assigned work orders.`}
          </Alert>
        )}
        <MiAlert
          description={
            // eslint-disable-next-line no-nested-ternary
            !hasSelectedCalendarSet
              ? "Select a calendar set in Work Order Details to see assignable users."
              : !hasAssignableUsers
              ? "The selected calendar set has no assignable users yet."
              : "Determine how you want the auto assigned work orders to be distributed."
          }
          storageKey="configWorkOrderAssignmentRatioAlert"
        />
      </ConfigAccordionHeader>

      <ConfigAccordionHeader title="Auto-Assignment Time Window">
        <FormControl
          disabled={isReadOnly || !autoAssign || !canConfigureAutoAssign}
        >
          <FormLabel>Auto-Assignment Time Window</FormLabel>
          <RadioGroup
            row
            value={String(allDayAssignment)}
            onChange={(event) =>
              handleAllDayAssignmentChange(event.target.value)
            }
          >
            <FormControlLabel
              value="true"
              control={<Radio />}
              label="All Day"
            />
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Custom Time Windows"
            />
          </RadioGroup>
        </FormControl>
        {!allDayAssignment && (
          <>
            {autoAssignmentWindows.map((window, index) => (
              <Box
                key={`${window.start ?? "start"}-${
                  window.end ?? "end"
                }-${index}`}
              >
                <Box className={classes.windowContainer}>
                  <MiDetailFields
                    containerStyle={classes.timeInputFields}
                    detailFields={getCustomTimeWindowFields(index)}
                  />
                  <MiButton
                    title=""
                    sx={{
                      borderRadius: "50%",
                      minWidth: 32,
                      width: 32,
                      height: 32,
                    }}
                    color={BUTTON_COLORS.ERROR}
                    variant={BUTTON_VARIANTS.CONTAINED}
                    startIcon={{ path: mdiMinus }}
                    onClick={() => handleRemoveWindow(index)}
                    disabled={
                      isReadOnly ||
                      !autoAssign ||
                      !canConfigureAutoAssign ||
                      autoAssignmentWindows.length === 1
                    }
                  />
                </Box>
                {overlappingWindowIndices.has(index) && (
                  <Typography
                    variant="caption"
                    color="warning.main"
                    sx={{ ml: 3, mb: 2, display: "block" }}
                  >
                    Overlapping assignment window
                  </Typography>
                )}
              </Box>
            ))}
            <MiButton
              sx={{ mb: 2 }}
              title="Add Time Window"
              variant={BUTTON_VARIANTS.OUTLINED}
              startIcon={{ path: mdiPlus }}
              onClick={handleAddWindow}
              disabled={isReadOnly || !autoAssign || !canConfigureAutoAssign}
            />
          </>
        )}
        <MiAlert
          description="Define one or more time ranges during which this work order can be automatically assigned."
          storageKey="configWorkOrderAssignmentTimeWindowAlert"
        />
      </ConfigAccordionHeader>
    </Box>
  );
};

interface WorkOrderAssignmentRulesTabViewProps {
  form: UseFormReturn<IWorkOrderTemplateData>;
  extraTabProps?: {
    isReadOnly?: boolean;
    calendarSets?: ICalendarSet[];
  };
}

const useStyles = makeStyles()((theme) => ({
  list: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  windowContainer: {
    padding: theme.spacing(0, 2),
    justifyContent: "space-between",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  timeInputFields: {
    width: "100%",
  },
}));
